/**
 * Map Typesense search response to Algolia-shaped results for SearchContext compatibility.
 */
import { mapTypesenseDocumentToAlgoliaHit } from './map-typesense-document-to-algolia-hit';

/** @deprecated Use mapTypesenseDocumentToAlgoliaHit */
export function mapTypesenseHitToAlgoliaHit(
  document: Record<string, unknown>,
): Record<string, unknown> | null {
  return mapTypesenseDocumentToAlgoliaHit(document);
}

export interface TypesenseSearchResponseLike {
  found?: number;
  hits?: Array<{ document?: Record<string, unknown> } & Record<string, unknown>>;
  page?: number;
  facet_counts?: Array<{
    field_name: string;
    counts: Array<{ value: string; count: number }>;
  }>;
  search_time_ms?: number;
  request_params?: { per_page?: number | string };
}

export interface AlgoliaSearchResultLike {
  hits: Record<string, unknown>[];
  nbHits: number;
  page: number;
  nbPages: number;
  hitsPerPage: number;
  query: string;
  params: string;
  processingTimeMS: number;
  facets?: Record<string, Record<string, number>>;
}

export function mapTypesenseResponseToAlgoliaShape(
  response: TypesenseSearchResponseLike,
  query: string,
): AlgoliaSearchResultLike {
  const hits = (response.hits ?? [])
    .map((hit) => {
      const document = (hit.document ?? hit) as Record<string, unknown>;
      return mapTypesenseDocumentToAlgoliaHit(document);
    })
    .filter((mapped): mapped is Record<string, unknown> => mapped !== null);

  const perPageRaw = response.request_params?.per_page;
  const hitsPerPage =
    typeof perPageRaw === 'number'
      ? perPageRaw
      : typeof perPageRaw === 'string'
        ? Number(perPageRaw) || 24
        : 24;

  const found = response.found ?? hits.length;
  const page = Math.max(0, (response.page ?? 1) - 1);
  const nbPages = hitsPerPage > 0 ? Math.ceil(found / hitsPerPage) : 0;

  const facets: Record<string, Record<string, number>> = {};
  for (const facet of response.facet_counts ?? []) {
    facets[facet.field_name] = {};
    for (const count of facet.counts) {
      facets[facet.field_name][count.value] = count.count;
    }
  }

  return {
    hits,
    nbHits: found,
    page,
    nbPages,
    hitsPerPage,
    query,
    params: '',
    processingTimeMS: response.search_time_ms ?? 0,
    facets: Object.keys(facets).length > 0 ? facets : undefined,
  };
}
